import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import api from '../../api/client';
import StatusBadge from '../common/StatusBadge';
import ConfirmDialog from '../common/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'processing', label: 'Procesando' },
  { value: 'ready', label: 'Listo' },
  { value: 'error', label: 'Error' },
];

export default function EpisodeList({ showAll = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [episodes, setEpisodes] = useState([]);
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [selectedShow, setSelectedShow] = useState(id || '');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmItem, setConfirmItem] = useState(null);

  useEffect(() => {
    if (showAll) {
      fetchShows();
    }
    fetchEpisodes();
  }, []);

  useEffect(() => {
    fetchEpisodes();
  }, [selectedShow, statusFilter]);

  const fetchShows = async () => {
    try {
      const { data } = await api.get('/api/shows', { params: { is_active: true } });
      setShows(data);
    } catch (err) {
      console.error('[EpisodeList] Failed to fetch shows:', err);
    }
  };

  const fetchEpisodes = async () => {
    setLoading(true);
    try {
      const params = { status: statusFilter === 'all' ? undefined : statusFilter };
      let url = '/api/episodes';
      if (!showAll && selectedShow) {
        url = `/api/shows/${selectedShow}/episodes`;
      } else if (showAll && selectedShow) {
        params.show_id = selectedShow;
      }
      const { data } = await api.get(url, { params });
      setEpisodes(data);
    } catch (err) {
      console.error('[EpisodeList] Failed to fetch episodes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (ep) => {
    try {
      await api.delete(`/api/episodes/${ep.id}`);
      setEpisodes(episodes.filter((e) => e.id !== ep.id));
    } catch (err) {
      console.error('[EpisodeList] Delete failed:', err);
    }
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setSearchParams({ status: value });
  };

  const handleShowChange = (value) => {
    setSelectedShow(value);
    if (!showAll) {
      navigate(`/shows/${value}/episodes`);
    }
  };

  const canEdit = (ep) => {
    if (user?.role === 'admin' || user?.role === 'operator' || user?.role === 'editor') return true;
    if (showAll) return false;
    return ep.show_id === parseInt(id, 10) || user?.role === 'dj';
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Cargando episodios...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">
          {showAll ? 'Todos los Episodios' : 'Episodios'}
        </h1>
        <Link to={showAll ? '/episodes?status=upload' : `/shows/${id}/episodes/upload`} className="btn btn-primary btn-sm">
          Subir Episodio
        </Link>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3">
        {showAll && (
          <select
            value={selectedShow}
            onChange={(e) => handleShowChange(e.target.value)}
            className="input w-48 text-sm"
          >
            <option value="">Todas las series</option>
            {shows.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        )}
        <select
          value={statusFilter}
          onChange={(e) => handleStatusFilterChange(e.target.value)}
          className="input w-40 text-sm"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {episodes.length === 0 ? (
        <div className="py-12 text-center text-dark-400">
          No se encontraron episodios.
          {!showAll && (
            <Link to={`/shows/${id}/episodes/upload`} className="ml-2 text-primary-400 hover:text-primary-300">
              Sube el primero
            </Link>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Título</th>
                <th>#</th>
                <th>Programado</th>
                <th>Estado</th>
                <th>Fecha de Subida</th>
                <th>Duración</th>
                <th className="text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {episodes.map((ep) => (
                <tr key={ep.id}>
                  <td className="font-medium text-dark-100">{ep.title}</td>
                  <td className="text-dark-400">
                    {ep.episode_number ? `#${ep.episode_number}` : '—'}
                  </td>
                  <td className="text-sm text-dark-300">
                    {ep.scheduled_date ? format(parseISO(ep.scheduled_date), 'PPp') : '—'}
                  </td>
                  <td>
                    <StatusBadge status={ep.status} size="sm" />
                  </td>
                  <td className="text-sm text-dark-400">
                    {ep.created_at ? format(parseISO(ep.created_at), 'PPp') : '—'}
                  </td>
                  <td className="text-sm text-dark-400">
                    {ep.duration_seconds ? `${ep.duration_seconds}s` : '—'}
                  </td>
                  <td className="text-right">
                    {canEdit(ep) && (
                      <>
                        <Link
                          to={`/shows/${ep.show_id}/episodes/${ep.id}/edit`}
                          className="text-xs text-dark-400 hover:text-dark-200"
                          title="Editar"
                        >
                          ✎
                        </Link>
                        <span className="mx-1 text-dark-600">|</span>
                        <button
                          onClick={() => {
                            setConfirmItem(ep);
                            setConfirmOpen(true);
                          }}
                          className="text-xs text-error-400 hover:text-error-300"
                          title="Eliminar"
                        >
                          ✕
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Eliminar Episodio"
        message={`¿Seguro que quieres eliminar "${confirmItem?.title}"? Esta acción no se puede deshacer.`}
        danger
        onConfirm={() => confirmItem && handleDelete(confirmItem)}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
}
